// src/views/operateur/OperateurReglages.jsx — LES RÉGLAGES DE L'IA, DANS LA
// DESTINATION.
//
// LE PIVOT (Dimitri 2026-09-03) : « une SEULE destination qui contient tout
// (conversation, décisions en attente, historique, plans récurrents,
// RÉGLAGES) ; l'onglet IA des Réglages disparaît ».
//
// CE QUI A DÉMÉNAGÉ, ET CE QUI N'EXISTAIT DÉJÀ PLUS. Le pivot parlait
// d'« interrupteurs déménagés » — il n'y en avait plus AUCUN à déménager : le
// lot 4 opérateur a retiré le bloc prospection (interrupteurs morts + purge de
// masse remplacée par la purge gardée), et la clé Claude est passée côté
// serveur bien avant. Ce qui restait dans l'onglet IA de ParamsPanel était de
// la PROSE : ce que l'IA fait, ce qui tourne tout seul, où vivent les clés.
// C'est cette prose qui vient ici — sa maison, désormais.
//
// ⚠️ TEXTES D'AIDE = ÉTAT ACTUEL (règle permanente). La carte a été RECOMPTÉE
// ligne à ligne le 2026-09-28 dans `vercel.json` et `.github/workflows/` —
// elle en annonçait trois alors que l'assistant en fait tourner huit.
//
// SON PÉRIMÈTRE EST CELUI DE L'ASSISTANT, ET ELLE LE DIT. Le décompte total
// est de 37 (24 crons `vercel.json` + 13 lignes `- cron:` dans
// `.github/workflows/`, comptées le 2026-09-28) : annoncer « huit »
// sans borner remplacerait un faux « trois » par un faux « huit ». Et surtout,
// « aucune n'écrit à un voyageur » est FAUSSE sur l'ensemble —
// `direct-journey-daily`/`-evening` (rappels caution, message de départ) et
// `post-stay-marketing` (promo aux voyageurs OTA partis) écrivent bien à des
// voyageurs. Ils sont dormants par INTERRUPTEUR, pas par nature : la phrase
// affirmerait une propriété des tâches, pas un état des flags. C'est
// exactement le défaut que ce lot corrige — d'où la borne explicite, plutôt
// qu'une liste de 37 lignes que personne ne relira.
// Le « `0 2` et `0 14` UTC » écrit ici le 2026-09-06 était faux aussi :
// `scrape-prospects` n'a qu'une seule entrée, `0 2 * * *`.
//
//   · rapport matinal        → vercel.json /api/cron/monitor        `0 7 * * *` UTC → 9 h
//   · relevé d'annonces      → vercel.json /api/scrape-prospects    `0 2 * * *` UTC  → 4 h
//   · webhook Melo           → api/melo-webhook.js — entrant, pas un cron ;
//                              EN PAUSE depuis le 01/10/2026 (src/lib/service-annonces.js)
//   · cartes à décider       → vercel.json /api/cron/operateur-propose       `*/5`
//   · relevé de la boîte     → vercel.json /api/cron/operateur-mail-ingest   `*/15`
//   · classement des mails   → vercel.json /api/cron/operateur-mail-verdict  `*/10`
//   · oubli des cartes       → vercel.json /api/cron/operateur-purge      `45 4` UTC → 6 h 45
//   · oubli des mails        → vercel.json /api/cron/operateur-mail-purge `50 4` UTC → 6 h 50
//
// Heures données en heure de PARIS (heure d'été ; en hiver, décaler d'une
// heure). Si un cron bouge, ce texte bouge — sinon il ment.
//
// « Aucune n'écrit à un voyageur ni à un prospect » RE-VÉRIFIÉE le 2026-09-28
// sur le code d'aujourd'hui : operateur-propose ne fait qu'ÉCRIRE des lignes
// `operateur_propositions` (aucun envoi — l'envoi part du clic de Dimitri) ;
// ingest lit l'IMAP en EXAMINE (lecture seule) ; verdict lit et classe ; les
// deux purges n'effacent que chez nous ; les crons d'emails de prospection ont
// été SUPPRIMÉS au lot 4 opérateur B.

import { Lock, ShieldCheck, Sparkles } from 'lucide-react'
import { DS } from '../../lib/ui'
import { accord, nomAccorde } from '../../lib/accord-nombres'

const carte = {
  background: '#fff', border: '1px solid #e5e8ec', borderRadius: 14,
  padding: 16, marginBottom: 12,
}
const titre = {
  fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600,
  fontSize: 15, color: DS?.textPrimary || '#1b3a5c', margin: '0 0 3px',
}
const sous = { fontSize: 11.5, color: DS?.textMuted || '#5c6773', margin: '0 0 12px', lineHeight: 1.6 }
const ligne = { fontSize: 12, color: DS?.textSecondary || '#233240', lineHeight: 1.9 }

// ── LES VAGUES DE GESTES — CE QUI EST OUVERT EN PRODUCTION, EN LECTURE SEULE ─
//
// Dimitri, 2026-09-14 : « fais-les apparaître quelque part. Ne pas savoir ce
// qui est ouvert en production est un angle mort. »
//
// ⚠️ ON MONTRE, ON NE PILOTE PAS. Cette carte n'a ni bouton, ni champ, ni
// appel : elle rend `etat_operateur.vagues`, dérivé côté serveur de
// `OPERATEUR_GESTES_VAGUE` à l'instant de l'appel (lib/operateur-intention.js,
// `lireVaguesOperateur`). Ouvrir une vague est une décision de Dimitri posée
// dans l'environnement Vercel — « je ne veux pas qu'il puisse le lever ». Un
// test grave qu'aucun chemin d'écriture ne part d'ici.
//
// ⚠️ ET ELLE NE DEVINE RIEN (règle de LigneOrganes) : si le serveur n'envoie
// pas le bloc, elle le DIT au lieu d'afficher un défaut plausible.
function CarteVagues({ vagues }) {
  const recu = vagues && typeof vagues === 'object' && Array.isArray(vagues.vagues)
  const ouvertes = recu ? vagues.vagues.filter((v) => v.ouverte === true) : []
  const fermees = recu ? vagues.vagues.filter((v) => v.ouverte !== true) : []
  const src = recu ? vagues.source || {} : {}
  return (
    <div style={carte} data-testid="operateur-reglages-vagues">
      <p style={titre}>
        <Lock size={14} strokeWidth={1.9} style={{ verticalAlign: -2, marginRight: 6 }} />
        Ce qu&apos;il a le droit de proposer — les vagues de gestes
      </p>
      <p style={sous}>
        Les gestes s&apos;ouvrent un par un, sur votre décision — une vague
        peut n&apos;en avoir qu&apos;un seul d&apos;ouvert.
        <strong> Lu dans l&apos;environnement du déploiement, pas réglable ici</strong> —
        cet écran montre, il ne pilote pas.
      </p>

      {!recu ? (
        <div style={ligne} data-testid="vagues-non-recues">
          Le serveur n&apos;a pas envoyé l&apos;état des vagues — rien n&apos;est
          affiché plutôt qu&apos;une valeur devinée.
        </div>
      ) : (
        <>
          <div style={ligne} data-testid="vagues-interrupteur"
            data-etat={vagues.gestes_allumes === true ? 'allume' : 'eteint'}>
            · Interrupteur général des gestes (<code>{src.interrupteur || 'OPERATEUR_GESTES_ENABLED'}</code>) :{' '}
            <strong>{vagues.gestes_allumes === true ? 'allumé' : 'éteint'}</strong>
            {vagues.gestes_allumes !== true && (
              <> — en conversation, aucun geste n&apos;est composé tant qu&apos;il est éteint.</>
            )}
          </div>
          <div style={ligne} data-testid="vagues-source">
            · Variable <code>{src.variable || 'OPERATEUR_GESTES_VAGUE'}</code> :{' '}
            {src.renseignee ? (
              <>valeur lue <code data-testid="vagues-valeur">{src.valeur}</code></>
            ) : (
              <><strong>absente</strong> — défaut appliqué : <strong data-testid="vagues-defaut">{src.defaut || 'aucune vague ouverte'}</strong></>
            )}
          </div>
          <ul data-testid="vagues-liste"
            style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
            {vagues.vagues.map((v) => {
              const on = v.ouverte === true
              // Levier par levier (2026-09-15) : une vague CANDIDATE (« v: »)
              // n'est ni ouverte ni fermée — on dit exactement ce qui est ouvert.
              const partielle = !on && v.partielle === true
              const ouverts = Array.isArray(v.ouverts) ? v.ouverts : []
              const etat = on ? 'ouverte' : partielle ? 'partielle' : 'fermee'
              return (
                <li key={v.vague} data-testid={`vague-${v.vague}`} data-etat={etat}
                  style={{ ...ligne, display: 'flex', gap: 8, alignItems: 'baseline',
                    color: on || partielle ? (DS?.textPrimary || '#1b3a5c') : (DS?.textMuted || '#5c6773') }}>
                  {/* Le mot porte l'information, jamais la couleur seule. */}
                  <span style={{ minWidth: 52, fontSize: 10.5, fontWeight: 600 }}>
                    {on ? 'ouverte' : partielle ? 'candidate' : 'fermée'}
                  </span>
                  <span>
                    Vague {v.vague} — {v.libelle}{' '}
                    <span style={{ opacity: 0.75 }}>
                      ({v.leviers} levier{v.leviers > 1 ? 's' : ''})
                    </span>
                    {partielle && (
                      <span data-testid={`vague-${v.vague}-ouverts`} style={{ display: 'block', fontSize: 10.5, marginTop: 2 }}>
                        {ouverts.length === 0
                          ? 'aucun levier ouvert — chacun s\'ouvre un par un, sur votre mot'
                          : `${ouverts.length} levier${ouverts.length > 1 ? 's' : ''} ouvert${ouverts.length > 1 ? 's' : ''} sur ${v.leviers} : ${ouverts.join(', ')}`}
                      </span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
          <p style={{ ...sous, marginTop: 10, marginBottom: 0 }} data-testid="vagues-resume">
            {ouvertes.length === 0
              ? 'Aucune vague ouverte : il ne peut proposer aucun geste.'
              : `${accord(ouvertes.length, 'vague ouverte')} (${ouvertes.map((v) => v.vague).join(', ')}), ${accord(ouvertes.reduce((n, v) => n + v.leviers, 0), 'levier proposable')}`}
            {fermees.length > 0 && ` · ${nomAccorde(fermees.length, 'fermée')} : ${fermees.map((v) => v.vague).join(', ')}`}.
          </p>
        </>
      )}
    </div>
  )
}

export default function OperateurReglages({ vagues }) {
  return (
    <div data-testid="operateur-reglages">
      <CarteVagues vagues={vagues} />
      <div style={carte}>
        <p style={titre}>
          <Sparkles size={14} strokeWidth={1.9} style={{ verticalAlign: -2, marginRight: 6 }} />
          Ce qu&apos;il utilise
        </p>
        <p style={sous}>
          Claude, pour le scoring des annonces, l&apos;analyse business, le rapport
          matinal et les brouillons de démarchage (Logements › À démarcher).
        </p>
        <div style={{ ...ligne, display: 'flex', gap: 7, alignItems: 'flex-start' }}
          data-testid="operateur-reglages-cle">
          <ShieldCheck size={14} strokeWidth={1.9} style={{ flex: '0 0 auto', color: DS?.marine || '#215387', marginTop: 3 }} />
          {/* Audit 0A (30/09) : une phrase d'état simple, sans jargon (règle
              32) — ni nom de variable, ni « proxy ». L'ancienne phrase disait
              aussi « aucune clé n'est stockée dans l'application », ce qui
              était FAUX tant que `co.claudeKey` restait dans la configuration.
              Aucun champ de saisie de clé ici ni ailleurs (invariant e2e
              tests/e2e/cfg-form-ia-roles.spec.js). */}
          <span>La clé est gardée côté serveur.</span>
        </div>
      </div>

      <div style={carte}>
        <p style={titre}>Ce que l&apos;assistant fait tout seul</p>
        <p style={sous}>
          Sept tâches programmées et un webhook — <strong>les siennes
          uniquement</strong>. L&apos;application en fait tourner une trentaine
          d&apos;autres (réservations, cautions, ménage, effacements), qui ne
          sont pas listées ici. <strong>Aucune des huit ci-dessous n&apos;écrit
          à un voyageur ni à un prospect</strong> — le démarchage se travaille à
          la main dans Logements › À démarcher.
        </p>
        <div style={ligne} data-testid="operateur-reglages-automatismes">
          · <strong>Rapport matinal</strong>, 9 h — il analyse le parc et t&apos;envoie un mail.<br />
          · <strong>Relevé d&apos;annonces</strong>, 4 h — Google Maps, Pages Jaunes, FNAIM, IAD, Optimhome.<br />
          · <strong>Service d&apos;annonces</strong>, en pause depuis le 1er octobre — les annonces déjà récupérées restent consultables.<br />
          · <strong>Cartes à décider</strong>, toutes les 5 minutes — il prépare une réponse aux messages voyageurs restés sans réponse et la pose en attente de ta décision. Il n&apos;envoie rien lui-même.<br />
          · <strong>Relevé de la boîte mail</strong>, toutes les 15 minutes — il note qu&apos;un mail est arrivé, sans en lire le contenu.<br />
          · <strong>Classement des mails</strong>, toutes les 10 minutes — il lit le contenu, garde le classement, jette le texte.<br />
          · <strong>Oubli des cartes</strong>, 6 h 45 — passé 30 jours, le détail est effacé ; le résumé reste.<br />
          · <strong>Oubli des mails</strong>, 6 h 50 — passé 30 jours, l&apos;expéditeur et le sujet sont effacés ; la trace du mail reste.
        </div>
        <p style={{ ...sous, marginTop: 10, marginBottom: 0 }}>
          Les trois tâches de l&apos;assistant (cartes, relevé de la boîte,
          classement) ne font rien tant que leur interrupteur n&apos;est pas posé
          dans Vercel. Les deux oublis tournent <strong>même quand
          l&apos;opérateur est à l&apos;arrêt</strong> : effacer au bout de 30 jours
          est une promesse, pas une activité. Heures données en heure de Paris
          (heure d&apos;été ; en hiver, décaler d&apos;une heure).
        </p>
      </div>

      <div style={carte}>
        <p style={titre}>Où vivent les secrets</p>
        <p style={{ ...sous, marginBottom: 0 }}>
          Dans les variables d&apos;environnement Vercel — jamais dans
          l&apos;application, jamais exposées publiquement.
        </p>
      </div>
    </div>
  )
}
